import { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';

import { Dialog, InputText, Button } from 'components';
import { getFieldFormat, fieldFormatHint, routes } from 'constants/index';

import { ListOneTicket } from '../../ListOne.types';
import { TicketField } from 'types';

interface ListOneFixAndPromoteDialogProps {
  visible: boolean;
  ticket: ListOneTicket | null;
  onHide: () => void;
  onSubmit: (fixData: any) => void;
  isLoading: boolean;
}

// Fallback for legacy tickets created before `required` was stamped on fields.
// Covers only the always-mandatory fields — conditional rules default to optional.
const ALWAYS_MANDATORY_FIELDS = new Set([
  'SerialNo', 'COUNTRY', 'COMPANYCODE', 'COMPANYNAME', 'BUCODE', 'BUNAME',
  'AZADEAID', 'PERSONID', 'EMPFIRSTNAME', 'EMPFAMILYNAME',
  'RELATION', 'DOB', 'GENDER', 'NATIONALITY', 'MARITAL',
  'POSITION', 'CLASS', 'GRADE', 'USER_PERSON_TYPE', 'TIMESET',
]);

// Format hints come from the shared FIELD_FORMATS constant, which the
// Docs > Field formats page uses too, so the two stay in sync.
const placeholderFor = (field: TicketField, key: 'oldValue' | 'newValue') => {
  if (key === 'oldValue') return '';
  return fieldFormatHint(field.field);
};

// Always-visible expected-format hint under a field in error. A placeholder is
// not enough here: a format error means the box already holds a (wrong) value,
// so the placeholder never shows.
function FieldFormatHint({ field }: { field: string }) {
  const spec = getFieldFormat(field);
  if (!spec) return null;
  return (
    <div
      style={{
        marginTop: 2,
        padding: '6px 10px',
        background: '#f1f5f9',
        borderRadius: 4,
        fontSize: '1.15rem',
        color: '#334155',
        lineHeight: 1.5,
      }}
    >
      <span style={{ fontWeight: 600 }}>Expected format:</span> {spec.format}
      {spec.example ? (
        <>
          {' '}
          <span style={{ color: '#64748b' }}>e.g.</span>{' '}
          <code
            style={{
              background: '#ffffff',
              border: '1px solid #e2e8f0',
              borderRadius: 3,
              padding: '1px 5px',
              fontFamily: "'Courier New', monospace",
            }}
          >
            {spec.example}
          </code>
        </>
      ) : null}
      {spec.note ? (
        <div style={{ marginTop: 3, color: '#64748b' }}>{spec.note}</div>
      ) : null}
    </div>
  );
}

const isErrorField = (field: TicketField) =>
  field.type === 'mandatory' || field.type === 'format';

const isRequiredField = (field: TicketField) => {
  // A "mandatory" type means the backend flagged this field as a missing
  // required value — it's required by definition, regardless of how/whether
  // the `required` flag was stamped on the field.
  if (field.type === 'mandatory') return true;
  return field.required !== undefined
    ? field.required
    : ALWAYS_MANDATORY_FIELDS.has(field.field);
};

const errorBadge = (field: TicketField) => {
  if (field.type === 'mandatory') {
    return (
      <span
        style={{
          fontSize: '0.7rem',
          fontWeight: 600,
          color: '#b50000',
          background: '#ffe5e5',
          padding: '2px 6px',
          borderRadius: 4,
          marginLeft: 8,
        }}
      >
        MISSING
      </span>
    );
  }
  if (field.type === 'format') {
    return (
      <span
        style={{
          fontSize: '0.7rem',
          fontWeight: 600,
          color: '#b35c00',
          background: '#fff2e0',
          padding: '2px 6px',
          borderRadius: 4,
          marginLeft: 8,
        }}
      >
        INVALID FORMAT
      </span>
    );
  }
  return null;
};

export default function ListOneFixAndPromoteDialog({
  visible,
  ticket,
  onHide,
  onSubmit,
  isLoading,
}: ListOneFixAndPromoteDialogProps) {
  const [healthPolicyId, setHealthPolicyId] = useState('');
  const [lifePolicyId, setLifePolicyId] = useState('');
  const [editableFields, setEditableFields] = useState<TicketField[]>([]);
  const [leaveDate, setLeaveDate] = useState('');
  const [leaveReason, setLeaveReason] = useState('');

  const resetState = () => {
    setHealthPolicyId('');
    setLifePolicyId('');
    setEditableFields(ticket?.fields ? [...ticket.fields] : []);
    setLeaveDate(ticket?.affectedDate || '');
    const reasonField = ticket?.fields?.find((f) => f.field === 'LEAVEREASON');
    setLeaveReason(reasonField?.newValue || reasonField?.oldValue || '');
  };

  const handleFieldChange = (
    index: number,
    key: 'oldValue' | 'newValue',
    value: string
  ) => {
    setEditableFields((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [key]: value };
      return updated;
    });
  };

  const handleSubmit = () => {
    if (!ticket) return;

    switch (ticket.statusId) {
      case 3:
        if (ticket.type === 'DE') {
          onSubmit({ leaveDate, leaveReason });
        } else {
          onSubmit({ fields: editableFields });
        }
        break;
      case 13:
        onSubmit({ healthPolicyId });
        break;
      case 14:
        onSubmit({ lifePolicyId });
        break;
      case 51:
        onSubmit({});
        break;
      default:
        break;
    }
  };

  const renderContent = () => {
    if (!ticket) return null;

    switch (ticket.statusId) {
      case 13:
        return (
          <div className="field">
            <label htmlFor="healthPolicyId">Health Policy ID</label>
            <InputText
              name="healthPolicyId"
              value={healthPolicyId}
              onChange={(e) => setHealthPolicyId(e.target.value)}
              placeholder="Enter Health Policy ID"
            />
          </div>
        );

      case 14:
        return (
          <div className="field">
            <label htmlFor="lifePolicyId">Life Policy ID</label>
            <InputText
              name="lifePolicyId"
              value={lifePolicyId}
              onChange={(e) => setLifePolicyId(e.target.value)}
              placeholder="Enter Life Policy ID"
            />
          </div>
        );

      case 3:
        if (ticket.type === 'DE') {
          return (
            <div className="flex flex-column gap-3">
              <div className="field">
                <label htmlFor="leaveDate">Leave Date (DD/MM/YYYY)</label>
                <InputText
                  name="leaveDate"
                  value={leaveDate}
                  onChange={(e) => setLeaveDate(e.target.value)}
                  placeholder="e.g. 15/04/2026"
                  style={{ width: '100%' }}
                />
              </div>
              <div className="field">
                <label htmlFor="leaveReason">Leave Reason</label>
                <InputText
                  name="leaveReason"
                  value={leaveReason}
                  onChange={(e) => setLeaveReason(e.target.value)}
                  placeholder="e.g. Resignation"
                  style={{ width: '100%' }}
                />
              </div>
            </div>
          );
        }
        return (
          <GroupedFields
            editableFields={editableFields}
            onFieldChange={handleFieldChange}
          />
        );

      case 51:
        return (
          <p>
            Are you sure you want to release this DE ticket from the buffer
            queue? Employee ID:{' '}
            <span style={{ color: '#b50000' }}>{ticket.employeeId}</span>
          </p>
        );

      default:
        return null;
    }
  };

  const footer = (
    <div className="flex justify-content-end gap-2">
      <Button label="Cancel" outlined onClick={onHide} disabled={isLoading} />
      <Button
        label="Fix & Promote"
        onClick={handleSubmit}
        loading={isLoading}
      />
    </div>
  );

  return (
    <Dialog
      header={
        ticket
          ? `Fix & Promote - ${ticket.type} | ${ticket.employeeId} | ${ticket.statusDescription}`
          : 'Fix & Promote'
      }
      footer={footer}
      onHide={onHide}
      visible={visible}
      onShow={resetState}
      style={{ width: '50vw' }}
      position="top"
    >
      {renderContent()}
    </Dialog>
  );
}

interface GroupedFieldsProps {
  editableFields: TicketField[];
  onFieldChange: (
    index: number,
    key: 'oldValue' | 'newValue',
    value: string
  ) => void;
}

function GroupedFields({ editableFields, onFieldChange }: GroupedFieldsProps) {
  const {
    requiredFields,
    optionalFields,
    errorCount,
    requiredErrors,
    optionalErrors,
    mandatoryErrorNames,
    formatErrorNames,
  } = useMemo(() => {
    const req: Array<{ field: TicketField; originalIndex: number }> = [];
    const opt: Array<{ field: TicketField; originalIndex: number }> = [];
    const mandatoryNames: string[] = [];
    const formatNames: string[] = [];
    let errors = 0;
    let reqErr = 0;
    let optErr = 0;
    editableFields.forEach((field, originalIndex) => {
      const isErr = isErrorField(field);
      if (isErr) {
        errors++;
        if (field.type === 'mandatory') mandatoryNames.push(field.field);
        else if (field.type === 'format') formatNames.push(field.field);
      }
      if (isRequiredField(field)) {
        if (isErr) reqErr++;
        req.push({ field, originalIndex });
      } else {
        if (isErr) optErr++;
        opt.push({ field, originalIndex });
      }
    });
    // Errors surface first within each group
    const byError = (
      a: { field: TicketField },
      b: { field: TicketField }
    ) => Number(isErrorField(b.field)) - Number(isErrorField(a.field));
    req.sort(byError);
    opt.sort(byError);
    return {
      requiredFields: req,
      optionalFields: opt,
      errorCount: errors,
      requiredErrors: reqErr,
      optionalErrors: optErr,
      mandatoryErrorNames: mandatoryNames,
      formatErrorNames: formatNames,
    };
  }, [editableFields]);

  // Mandatory section auto-opens; Optional stays collapsed unless it has errors.
  const [mandatoryOpen, setMandatoryOpen] = useState(true);
  const [optionalOpen, setOptionalOpen] = useState(optionalErrors > 0);

  const renderFieldRow = (
    field: TicketField,
    originalIndex: number
  ) => {
    const hasError = isErrorField(field);
    const borderColor = hasError
      ? field.type === 'mandatory'
        ? '#b50000'
        : '#b35c00'
      : 'transparent';
    return (
      <div
        key={field._id}
        className="flex flex-column gap-2"
        style={{
          borderLeft: `3px solid ${borderColor}`,
          paddingLeft: hasError ? 10 : 13,
          background: hasError ? '#fafafa' : 'transparent',
          paddingTop: 6,
          paddingBottom: 6,
          borderRadius: 2,
        }}
      >
        <label className="font-bold" style={{ display: 'flex', alignItems: 'center' }}>
          {field.field}
          {errorBadge(field)}
        </label>
        <div className="flex gap-2">
          <div className="flex flex-column flex-1">
            <label className="text-sm text-500">Old Value</label>
            <InputText
              name={`oldValue-${originalIndex}`}
              value={field.oldValue ?? ''}
              onChange={(e) =>
                onFieldChange(originalIndex, 'oldValue', e.target.value)
              }
              placeholder={placeholderFor(field, 'oldValue')}
            />
          </div>
          <div className="flex flex-column flex-1">
            <label className="text-sm text-500">New Value</label>
            <InputText
              name={`newValue-${originalIndex}`}
              value={field.newValue ?? ''}
              onChange={(e) =>
                onFieldChange(originalIndex, 'newValue', e.target.value)
              }
              placeholder={placeholderFor(field, 'newValue')}
            />
          </div>
        </div>
        {hasError && <FieldFormatHint field={field.field} />}
      </div>
    );
  };

  const sectionHeader = (
    title: string,
    count: number,
    errorCount: number,
    color: string,
    open: boolean,
    onToggle: () => void
  ) => (
    <div
      onClick={onToggle}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onToggle();
        }
      }}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        paddingBottom: 6,
        borderBottom: `2px solid ${color}`,
        marginBottom: 8,
        cursor: 'pointer',
        userSelect: 'none',
      }}
    >
      <span
        style={{
          display: 'inline-block',
          width: 12,
          color,
          fontSize: '0.75rem',
          transform: open ? 'rotate(90deg)' : 'rotate(0deg)',
          transition: 'transform 0.15s ease',
        }}
      >
        ▶
      </span>
      <span style={{ fontWeight: 700, color, fontSize: '1.15rem' }}>
        {title}
      </span>
      <span style={{ color: '#666', fontSize: '1rem' }}>
        {count} field{count === 1 ? '' : 's'}
        {errorCount > 0
          ? ` • ${errorCount} error${errorCount === 1 ? '' : 's'}`
          : ''}
      </span>
      <span
        style={{
          marginLeft: 'auto',
          color: '#888',
          fontSize: '0.8rem',
        }}
      >
        {open ? 'Hide' : 'Show'}
      </span>
    </div>
  );

  return (
    <div className="flex flex-column gap-4">
      {errorCount > 0 && (
        <div
          style={{
            padding: '12px 16px',
            background: '#fff2f2',
            border: '1px solid #f5c6c6',
            borderRadius: 4,
            fontSize: '1rem',
            fontWeight: 500,
            color: '#7a0000',
          }}
        >
          {errorCount} field{errorCount === 1 ? '' : 's'} need
          {errorCount === 1 ? 's' : ''} attention.
          {mandatoryErrorNames.length > 0 && (
            <div style={{ marginTop: 6 }}>
              <strong>Missing (must fix before promotion):</strong>{' '}
              {mandatoryErrorNames.join(', ')}
            </div>
          )}
          {formatErrorNames.length > 0 && (
            <div style={{ marginTop: 6 }}>
              <strong>Invalid format:</strong> {formatErrorNames.join(', ')}
            </div>
          )}
          <div style={{ marginTop: 8, fontWeight: 400 }}>
            The expected format is shown under each field below.{' '}
            <Link
              to={`${routes.docs.index}/${routes.docs.fields}`}
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: '#1d4ed8', fontWeight: 600 }}
            >
              See all field formats
            </Link>
          </div>
        </div>
      )}

      {requiredFields.length > 0 && (
        <div className="flex flex-column gap-2">
          {sectionHeader(
            'Mandatory Fields',
            requiredFields.length,
            requiredErrors,
            '#b50000',
            mandatoryOpen,
            () => setMandatoryOpen((v) => !v)
          )}
          {mandatoryOpen && (
            <div className="flex flex-column gap-3">
              {requiredFields.map(({ field, originalIndex }) =>
                renderFieldRow(field, originalIndex)
              )}
            </div>
          )}
        </div>
      )}

      {optionalFields.length > 0 && (
        <div className="flex flex-column gap-2">
          {sectionHeader(
            'Optional Fields',
            optionalFields.length,
            optionalErrors,
            '#888',
            optionalOpen,
            () => setOptionalOpen((v) => !v)
          )}
          {optionalOpen && (
            <div className="flex flex-column gap-3">
              {optionalFields.map(({ field, originalIndex }) =>
                renderFieldRow(field, originalIndex)
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
